import React, { memo, useEffect, useRef, useState } from "react"
import { graphql, navigate } from "gatsby"
import styled from "@emotion/styled"
import { documentToReactComponents } from "@contentful/rich-text-react-renderer"
import Layout from "components/layout"
import Container from "components/container"
import { mediaBreakpoint, BreakPoints } from "settings/breakpoints"
import ModuleRender from "components/ModuleRender"
import SEO, { isBrowser } from "components/SEO"
import { ArticleRichTextOptions } from "utils/richText"
import Media from "components/Media"
import { theme } from "common/colorScheme"
import Link from "components/Link"
import ArticlesForm from "components/Form/ArticlesForm"
import { css } from "@emotion/react"
import useRect from "utils/useRect"
import RelatedArticles from "components/RelatedArticles"
import { generateTagObjectFromString } from "components/ArticlesGrid"
import { Filter } from "components/ReferencesSection"
import { filterStyles } from "pages/articles"
import BlogPost from "components/Form/BlogPost"
import FacebookLogo from "../images/social/fb-purple.svg"
import TwitterLogo from "../images/social/twitter-purple.svg"
import LinkedInLogo from "../images/social/linkedin-purple.svg"

enum asideStates {
	fixedTop = "fixedTop",
	floating = "floating",
	fixedBottom = "fixedBottom",
}

interface IntersectionTriggerProps {
	margin: number
}

export const HeaderImage = styled.div`
	width: 100%;
	max-height: 400px;
	overflow: hidden;
	margin-top: ${theme.spacing(2)};
	margin-bottom: ${theme.spacing(3.5)};

	div {
		border-radius: 10px;
		overflow: hidden;
	}

	${mediaBreakpoint(BreakPoints.MD)} {
		margin-bottom: ${theme.spacing(5)};
	}
`

const MainWrapper = styled.div`
	display: flex;
	width: 100%;
	flex-direction: column;

	${mediaBreakpoint(BreakPoints.MD)} {
		flex-direction: row;
	}
`

const AsideContainer = styled.aside`
	position: relative;
	margin-bottom: ${theme.spacing(3.5)};

	${mediaBreakpoint(BreakPoints.MD)} {
		width: 15vw;
		max-width: 174px;
		margin-bottom: 0;
	}
`

const IntersectionTriggers = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	height: calc(100% - calc(50vh - 70px));
	width: 100%;
	margin-top: calc(50vh - 70px);
`

const IntersectionTriggerTop = styled.div<IntersectionTriggerProps>`
	height: ${({ margin }) => (margin ? `calc(100% + 50vh - ${margin}px - 70px)` : "100%")};
	width: 100%;
	opacity: 0.5;
`

const IntersectionTriggerBottom = styled.div<IntersectionTriggerProps>`
	position: absolute;
	height: 100%;
	width: 100%;
	opacity: 0.5;
`

const Aside = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;

	${mediaBreakpoint(BreakPoints.MD)} {
		flex-direction: column;
	}
`

const AsideLogo = styled.div`
	padding: ${theme.spacing(1)};
`

const Content = styled.div`
	width: 100%;
	max-width: 940px;
`

const ContentTop = styled.div`
	display: flex;
	justify-content: space-between;
	margin-bottom: ${theme.spacing(2)};
`

const ContentCategory = styled.span`
	text-transform: uppercase;
	font-size: 1.2em;
	font-weight: 800;

	a {
		color: ${theme.color.purple};
	}
`

const ContentDate = styled.span`
	font-size: 1.6em;
	color: ${theme.color.black};
`

const DesktopContentDate = styled(ContentDate)`
	display: none;
	${mediaBreakpoint(BreakPoints.MD)} {
		display: block;
	}
`

const MobileContentDate = styled(ContentDate)`
	display: block;
	margin-bottom: ${theme.spacing(2)};
	${mediaBreakpoint(BreakPoints.MD)} {
		display: none;
	}
`

const ContentTitle = styled.h1`
	color: ${theme.color.black};
	font-size: 4.8em;
	font-weight: 800;
	margin-bottom: ${theme.spacing(2.5)};
`

const ContentAuthor = styled.a`
	width: 100%;
	margin-bottom: ${theme.spacing(5)};
	display: flex;
	text-decoration: none;
	color: unset;
`

const ContentAuthorImage = styled.div`
	height: 60px;
	width: 60px;
	margin-right: ${theme.spacing(2)};
`

const ContentAutorText = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	font-size: 1.6em;
`

const ContentAuthorName = styled.div`
	font-weight: 800;
	margin-bottom: ${theme.spacing(1)};
`

const ContentAuthorTitle = styled.div``

export const Cover = styled.div`
	padding: 80px 0;
`

export const BottomContainer = styled.div`
	display: flex;
	justify-content: space-between;
	flex-direction: column;

	${mediaBreakpoint(BreakPoints.MD)} {
		flex-direction: row;
	}
`

export const ArticleContent = styled.article`
	a {
		color: var(--text-color);
		text-decoration: underline;
		font-weight: 700;
		&:hover {
			color: var(--main-color);
			text-decoration: underline;
		}
	}
	p {
		font-size: 2rem;
		line-height: 2.8rem;
		margin: 0 0 24px;
		&:empty {
			display: none;
		}
	}
	.twitter-tweet {
		opacity: 0;
	}
	.twitter-tweet.twitter-tweet-rendered {
		opacity: 1;
		transition: 0.2s opacity ease-in;
	}
	.instagram-container {
		width: 100%;
		display: flex;
		justify-content: center;
		margin: 50px 0;
	}
	ul,
	ol {
		font-size: 2rem;
		padding-left: 1rem;
		margin-left: 1rem;
	}
	li {
		margin: 0 0 1.2rem;
		line-height: 2.4rem;
	}
	h1 {
		font-size: 3.2rem;
		line-height: 3.8rem;
		font-weight: 800;
	}
	h2 {
		font-size: 2.4rem;
		line-height: 3rem;
		font-weight: 800;
	}
	h3 {
		font-size: 2rem;
		line-height: 2.8rem;
		font-weight: 800;
	}
	img {
		display: block;
		margin: 6.4rem auto;
	}
	iframe {
		max-width: 100%;
		width: 600px;
		margin: 6.4rem auto;
		display: block;
		border: none;
	}

	blockquote {
		opacity: 0.72;
		font-style: italic;
		> p:before {
			content: "\\201E";
		}
		> p:after {
			content: "\\201D";
		}
	}
`

const TagsContainer = styled.div`
	display: flex;
	flex-wrap: wrap;
	margin-bottom: ${theme.spacing(6)};

	> div {
		padding: 5px 20px !important;
		height: fit-content;

		&:first-of-type {
			margin-left: 0 !important;
		}
	}
`

const Article = ({ data, pageContext }) => {
	const [asideState, setAsideState] = useState(asideStates.fixedTop)
	const [topIntersecting, setTopIntersecting] = useState(false)
	const [bottomIntersecting, setBottomIntersecting] = useState(false)

	const asideRef = useRef(null)
	const triggerTopRef = useRef(null)
	const triggerBottomRef = useRef(null)
	const asideContainerRef = useRef(null)
	const { height: asideHeight } = useRect(asideRef)
	const { left: asideContainerLeft } = useRect(asideContainerRef)

	const { allContentfulBlogPost, contentfulBlogPost, contentfulForm } = data

	const image = contentfulBlogPost.coverImage || contentfulBlogPost.heroImage
	const { publishDate, title, tags, seo, authorRef, slug } = contentfulBlogPost
	const richtext = pageContext.markdown
	const form = contentfulForm

	const images = React.useMemo(() => {
		return data.allContentfulAsset.edges.reduce((acc, { node }) => {
			const {
				file: { url },
			} = node
			acc[url] = node
			return acc
		}, {})
	}, [data.allContentfulAsset])

	const html = React.useMemo(() => {
		return documentToReactComponents(richtext, ArticleRichTextOptions(images))
	}, [richtext, images])

	const tagObjects = tags.map(generateTagObjectFromString)

	const formattedDate = React.useMemo(() => {
		return new Date(publishDate).toLocaleDateString("en-US", {
			month: "long",
			day: "numeric",
			year: "numeric",
		})
	}, [publishDate])

	const getAsideStyles = (state: asideStates) => {
		switch (state) {
		case asideStates.floating:
			return css`
					${mediaBreakpoint(BreakPoints.MD)} {
						position: fixed;
						top: 70px;
						left: calc(${asideContainerLeft}px + 87px);
						transform: translateX(-50%);
					}
				`
		case asideStates.fixedBottom:
			return css`
					${mediaBreakpoint(BreakPoints.MD)} {
						position: absolute;
						bottom: 0;
						left: 50%;
						transform: translateX(-50%);
					}
				`
		default:
			return css``
		}
	}

	const mainTag = tags.length && tags[0]
	const tagSlug = mainTag.toLowerCase().replace(" ", "-")

	useEffect(() => {
		const triggerTop = triggerTopRef.current
		const triggerBottom = triggerBottomRef.current

		if (triggerTop) {
			// eslint-disable-next-line no-undef
			const callback: IntersectionObserverCallback = entries => {
				entries.forEach(entry => {
					if (entry.isIntersecting) {
						setTopIntersecting(true)
					} else {
						setTopIntersecting(false)
					}
				})
			}
			const observer = new IntersectionObserver(callback, {
				rootMargin: `-50% 0px`,
			})
			observer.observe(triggerTop)
		}

		if (triggerBottom) {
			// eslint-disable-next-line no-undef
			const callback: IntersectionObserverCallback = entries => {
				entries.forEach(entry => {
					if (entry.isIntersecting) {
						setBottomIntersecting(true)
					} else {
						setBottomIntersecting(false)
					}
				})
			}
			const observer = new IntersectionObserver(callback, {
				rootMargin: `-50% 0px`,
			})
			observer.observe(triggerBottom)
		}
	}, [])

	const handleLinkInPopup = link => window.open(link, "popup", "width=600,height=600,scrollbars=no,resizable=no")

	const handleFacebookShare = (url: string) => {
		if (isBrowser) {
			const { FB } = window as any
			if (FB) {
				FB.ui({
					method: "share",
					href: url,
				})
			}
		}
		return null
	}

	useEffect(() => {
		if (topIntersecting) {
			setAsideState(asideStates.floating)
		} else if (bottomIntersecting) {
			setAsideState(asideStates.fixedBottom)
		} else {
			setAsideState(asideStates.fixedTop)
		}
	}, [topIntersecting, bottomIntersecting])

	const handleClickFilter = (slugProp: string) => navigate(`/articles/?tag=${slugProp}/`)

	return (
		<>
			{seo && <SEO {...seo} />}
			<Layout footerConfig={{ variant: "default", hideDemo:false, hideNewsletter:false}}>
				<Container>
					<HeaderImage>
						<Media {...image} />
					</HeaderImage>
					<MainWrapper>
						<AsideContainer ref={asideContainerRef}>
							<Aside css={getAsideStyles(asideState)} ref={asideRef}>
								<AsideLogo>
									<Link
										to="https://www.facebook.com/latanabrand"
										target="_blank"
										onMouseDown={() => handleFacebookShare(`https://latana.com/post/${slug}`)}
									>
										<img src={FacebookLogo} alt="Facebook logo" />
									</Link>
								</AsideLogo>
								<AsideLogo>
									<Link
										to={`https://twitter.com/intent/tweet/?url=https://latana.com/post/${slug}&via=LatanaBrand`}
										target="_blank"
										onMouseDown={() =>
											handleLinkInPopup(
												`https://twitter.com/intent/tweet/?url=https://latana.com/post/${slug}&via=LatanaBrand`
											)
										}
									>
										<img src={TwitterLogo} alt="Twitter logo" />
									</Link>
								</AsideLogo>
								<AsideLogo>
									<Link
										to={`https://www.linkedin.com/sharing/share-offsite/?url=https://latana.com/post/${slug}/`}
										target="_blank"
										onMouseDown={() =>
											handleLinkInPopup(
												`https://www.linkedin.com/sharing/share-offsite/?url=https://latana.com/post/${slug}/`
											)
										}
									>
										<img src={LinkedInLogo} alt="LinkedIn logo" />
									</Link>
								</AsideLogo>
							</Aside>
							<IntersectionTriggers>
								<IntersectionTriggerTop margin={asideHeight} ref={triggerTopRef} />
								<IntersectionTriggerBottom margin={asideHeight} ref={triggerBottomRef} />
							</IntersectionTriggers>
						</AsideContainer>
						<Content>
							<ContentTop>
								{mainTag && (
									<ContentCategory>
										<Link to={`/articles/?page&tag=${tagSlug}/`}>{mainTag}</Link>
									</ContentCategory>
								)}
								<DesktopContentDate>{formattedDate}</DesktopContentDate>
							</ContentTop>
							<ContentTitle>{title}</ContentTitle>
							<MobileContentDate>{formattedDate}</MobileContentDate>
							{authorRef && (
								<ContentAuthor href={`/author/${authorRef.slug}/`}>
									{authorRef.picture && (
										<ContentAuthorImage>
											<Media {...authorRef.picture} />
										</ContentAuthorImage>
									)}
									<ContentAutorText>
										<ContentAuthorName>
											{authorRef.firstName} {authorRef.lastName}
										</ContentAuthorName>
										{authorRef.title && <ContentAuthorTitle>{authorRef.title}</ContentAuthorTitle>}
									</ContentAutorText>
								</ContentAuthor>
							)}
							<BlogPost bannerForm={contentfulBlogPost.bannerForm || {}} html={html} />
							<ModuleRender sections={contentfulBlogPost.sections} />
							<TagsContainer>
								{tagObjects.map(tagObject => {
									const { value, color, textColor, label } = tagObject
									return (
										<Filter
											key={value}
											css={[filterStyles]}
											color={color}
											textColor={textColor}
											onClick={() => handleClickFilter(value)}
										>
											{label}
										</Filter>
									)
								})}
							</TagsContainer>
						</Content>
					</MainWrapper>
				</Container>
				<RelatedArticles allContentfulBlogPost={allContentfulBlogPost} tags={tags} />
				<Container>
					{form && (
						<ArticlesForm
							title="Free Brand Insights and Tips"
							description="Sign up for our Newsletter to receive free, insightful tips on all things brand!"
							{...form}
						/>
					)}
				</Container>
				<script async src="https://platform.twitter.com/widgets.js" charSet="utf-8" />
			</Layout>
		</>
	)
}

export const query = graphql`
	fragment BlogPost on ContentfulBlogPost {
		publishDate
		slug
		author
		title
		seo {
			...SEO
		}
		heroImage {
			...Media800
		}
		coverImage {
			...MediaOriginal
		}
		description {
			description
		}
		content {
			content
		}
		tags
		authorRef {
			slug
			firstName
			lastName
			title
			picture {
				...MediaOriginal
			}
		}
	}

	query ($slug: String, $images: [String]) {
		contentfulBlogPost(slug: { eq: $slug }) {
			...BlogPost
			bannerForm {
				formId
				bannerGradient
				bannerImage {
				  file {
					url
				  }
				}
				hubspotFormId
				bannerSubtitle
				bannerTitle
				buttonVariant
				privacyConsent
				submitText
				contentfulfields {
					fieldName
					id
					fullWidth
					label
					options
					type
				  }
			  }
			  sections {
				... on ContentfulLandingSections {
					...Section
				}
			}
		}
		allContentfulBlogPost {
			edges {
				node {
					...BlogPost
				}
			}
		}
		allContentfulAsset(filter: { file: { url: { in: $images } }, node_locale: { eq: "en-US" } }) {
			edges {
				node {
					...ArticleImage
				}
			}
		}
		contentfulForm(formId: { eq: "newsletter" }) {
			...Form
		}
		
		contentfulPage(slug: { eq: "article-sections" }) {
			sections {
				... on ContentfulAlternatingCards {
					...AlternatingCards
				}
				... on ContentfulBrandImagesSection {
					...BrandImages
				}
				... on ContentfulBrandImagesWithReviews {
					...BrandImagesWithReviews
				}
				... on ContentfulLandingSections {
					...Section
				}
				... on ContentfulReferencesSection {
					...ReferencesSection
				}
			}
		}
	}
`

export default memo(Article)
